import { useEffect, useState, useCallback } from "react";
import {
  Gear,
  Bell,
  ArrowsClockwise,
  Info,
  CheckCircle,
  WarningCircle,
  Sun,
  Moon,
  Stethoscope,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import {
  fireNotification,
  getPermissionStatus,
  readLastPoll,
} from "../lib/notifications";
import { ensurePushSubscribed } from "../lib/push";

export default function Settings() {
  const { settings, saveSettings } = useApp();
  const [name, setName] = useState(settings.user_name || "");
  const [email, setEmail] = useState(settings.user_email || "");
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  useEffect(() => {
    setName(settings.user_name || "");
    setEmail(settings.user_email || "");
  }, [settings.user_name, settings.user_email]);

  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Notifications not supported on this device");
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === "granted") {
        await saveSettings({ notifications_enabled: true });
        toast.success("Notifications enabled");
        // V9.4 — subscribe to Web Push so reminders fire when app is closed
        ensurePushSubscribed().catch(() => {});
      } else {
        toast.error("Permission denied");
      }
    } catch (e) {
      toast.error("Failed to request permission");
    }
  };

  const toggleNotif = async (val) => {
    if (val && permission !== "granted") {
      await requestNotifPermission();
      return;
    }
    try {
      await saveSettings({ notifications_enabled: val });
    } catch {
      toast.error("Failed to save");
    }
  };

  const saveProfile = async () => {
    try {
      await saveSettings({ user_name: name, user_email: email });
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const forceRefresh = async () => {
    // V9.37 — Mark a one-shot intent so the next boot (after reload)
    // re-runs `ensurePushSubscribed` even if the user hasn't toggled
    // notifications. Belt-and-suspenders alongside the AppContext
    // boot reconciliation — covers the case where settings load is
    // delayed past the user navigating away.
    try {
      sessionStorage.setItem("journey:push:reconcile-on-boot", "1");
    } catch {}
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) await r.unregister();
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {}
    window.location.reload();
  };

  return (
    <div className="overflow-y-auto scroll-contain" data-testid="settings-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6">
        <div className="flex items-start gap-4 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
            <Gear size={22} weight="duotone" className="text-cyan-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Settings
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Customize Journey to fit you.
            </p>
          </div>
        </div>

        {/* Profile */}
        <Card title="Profile">
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What should Journey call you?"
              className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40"
              data-testid="profile-name-input"
            />
          </Field>
          <Field label="Timezone (used for reminder scheduling)">
            <Input
              value={settings.tz || "America/Los_Angeles"}
              onChange={(e) => saveSettings({ tz: e.target.value })}
              placeholder="America/Los_Angeles"
              className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40"
              data-testid="profile-tz-input"
            />
          </Field>
          <Field label="Email (for future reminder emails)">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40"
              data-testid="profile-email-input"
            />
          </Field>
          <div className="flex justify-end pt-2">
            <Button
              onClick={saveProfile}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
              data-testid="profile-save"
            >
              Save profile
            </Button>
          </div>
        </Card>

        {/* Appearance */}
        <Card title="Appearance">
          <Row
            icon={settings.theme === "light" ? Sun : Moon}
            title="Theme"
            desc={`Currently ${settings.theme === "light" ? "Light" : "Dark"}.`}
          >
            <div
              className="inline-flex bg-[#0f0f0f] border border-white/10 rounded-full p-0.5"
              data-testid="theme-toggle"
            >
              <button
                onClick={() => saveSettings({ theme: "dark" })}
                data-testid="theme-toggle-dark"
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  settings.theme !== "light"
                    ? "bg-cyan-500 text-black font-medium"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Dark
              </button>
              <button
                onClick={() => saveSettings({ theme: "light" })}
                data-testid="theme-toggle-light"
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  settings.theme === "light"
                    ? "bg-cyan-500 text-black font-medium"
                    : "text-white/60 hover:text-white"
                }`}
              >
                Light
              </button>
            </div>
          </Row>
        </Card>

        {/* Notifications */}
        <Card title="Notifications">
          <Row
            icon={Bell}
            title="Enable system notifications"
            desc="Show reminders as native notifications when due."
          >
            <Switch
              checked={settings.notifications_enabled && permission === "granted"}
              onCheckedChange={toggleNotif}
              data-testid="toggle-notifications"
            />
          </Row>
          <div className="px-1 pt-1">
            <PermissionBadge permission={permission} />
            {permission !== "granted" && permission !== "unsupported" && (
              <Button
                onClick={requestNotifPermission}
                variant="outline"
                size="sm"
                className="mt-2 border-white/10 hover:bg-white/5"
                data-testid="request-notif-permission"
              >
                Request permission
              </Button>
            )}
          </div>
        </Card>

        {/* Notification diagnostics */}
        <NotificationDiagnostics permission={permission} tz={settings.tz} />

        {/* App */}
        <Card title="App">
          <Row
            icon={ArrowsClockwise}
            title="Force refresh"
            desc="Clear cached data and reload the app."
          >
            <Button
              onClick={forceRefresh}
              variant="outline"
              size="sm"
              className="border-white/10 hover:bg-white/5"
              data-testid="force-refresh-button"
            >
              Refresh
            </Button>
          </Row>
          <Row
            icon={Info}
            title="About"
            desc="Journey · v1.0 · Modular AI assistant PWA."
          >
            <span className="text-xs text-white/40">v1.0</span>
          </Row>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <section className="rounded-2xl bg-[#0f0f0f] border border-white/5 p-5">
      <h2 className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-medium mb-4">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-white/55 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Row({ icon: Icon, title, desc, children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center shrink-0">
        <Icon size={16} weight="duotone" className="text-cyan-400/80" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14.5px] text-white/90">{title}</div>
        {desc && <div className="text-xs text-white/50 mt-0.5">{desc}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function PermissionBadge({ permission }) {
  if (permission === "granted")
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
        <CheckCircle size={14} weight="fill" /> Permission granted
      </span>
    );
  if (permission === "denied")
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-red-400">
        <WarningCircle size={14} weight="fill" /> Permission denied — enable in browser settings
      </span>
    );
  if (permission === "unsupported")
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-white/40">
        <WarningCircle size={14} /> Not supported on this device
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-white/50">
      <WarningCircle size={14} /> Permission not yet requested
    </span>
  );
}

/* ─── Notification diagnostics card ─────────────────────────────────────────
   Lets the user fire a real notification through the SAME pipeline reminders
   use, and shows a live snapshot of the PWA notification stack. The goal is
   to let us isolate where notifications are failing (permission / SW / page /
   delivery) without guessing.                                                */

const REASON_COPY = {
  "no-notification-api": {
    label: "Not supported",
    detail: "This browser doesn't expose the Notification API.",
    tone: "warn",
  },
  "permission-default": {
    label: "Permission required",
    detail: "Tap 'Request permission' first.",
    tone: "warn",
  },
  "permission-denied": {
    label: "Permission blocked",
    detail: "Enable in your browser/OS notification settings, then return.",
    tone: "error",
  },
  "sw-error": {
    label: "Service worker failed",
    detail: "SW is registered but didn't deliver. Try Force refresh below.",
    tone: "error",
  },
  "page-fallback-error": {
    label: "Display blocked",
    detail: "Both SW and in-page Notification failed. Likely OS/browser block.",
    tone: "error",
  },
  unknown: {
    label: "Unknown error",
    detail: "Something unexpected happened.",
    tone: "error",
  },
};

function NotificationDiagnostics({ permission, tz }) {
  const [diag, setDiag] = useState(() => readDiagSnapshot());
  const [testResult, setTestResult] = useState(null); // { ok, reason?, via?, at }
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setDiag(await readDiagSnapshotAsync());
  }, []);

  useEffect(() => {
    refresh();
    // Refresh diagnostics when tab becomes visible (catches SW registration changes)
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    // Refresh once after 1.5s — gives SW time to claim controller after first install
    const t = setTimeout(refresh, 1500);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clearTimeout(t);
    };
  }, [refresh]);

  // Re-read last-poll periodically while panel is mounted
  useEffect(() => {
    const id = setInterval(() => {
      const lp = readLastPoll();
      setDiag((d) => ({ ...d, lastPoll: lp }));
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const sendTest = async () => {
    setBusy(true);
    setTestResult(null);
    const result = await fireNotification("Journey · Test notification", {
      body: "If you see this on your home screen, notifications are wired.",
      tag: "journey-test-notification",
      data: { url: "/app/settings" },
      vibrate: [120, 60, 120],
    });
    const at = new Date().toISOString();
    setTestResult({ ...result, at });
    setBusy(false);
    if (result.ok) {
      toast.success(`Notification sent via ${result.via === "sw" ? "service worker" : "page"}`);
    } else {
      const copy = REASON_COPY[result.reason] || REASON_COPY.unknown;
      toast.error(copy.label);
    }
    // Auto-refresh diagnostics after test
    refresh();
  };

  return (
    <Card title="Notification diagnostics">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center shrink-0">
            <Stethoscope size={16} weight="duotone" className="text-cyan-400/80" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14.5px] text-white/90">Test notification</div>
            <div className="text-xs text-white/50 mt-0.5">
              Fires a real OS notification through the same path reminders use.
              Works in foreground or background.
            </div>
          </div>
          <Button
            onClick={sendTest}
            disabled={busy || permission === "unsupported"}
            size="sm"
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl shrink-0"
            data-testid="test-notification-button"
          >
            <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
            {busy ? "Sending…" : "Send"}
          </Button>
        </div>

        {testResult && <TestResultChip result={testResult} />}

        <div className="pt-2 border-t border-white/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-medium">
              Diagnostics
            </span>
            <button
              onClick={refresh}
              className="text-xs text-cyan-400/80 hover:text-cyan-300 p-2 -m-1"
              data-testid="diag-refresh"
            >
              Refresh
            </button>
          </div>
          <DiagList diag={diag} permission={permission} tz={tz} />
        </div>
      </div>
    </Card>
  );
}

function TestResultChip({ result }) {
  if (result.ok) {
    return (
      <div
        className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-xs text-emerald-300 flex items-center gap-2"
        data-testid="test-result-ok"
      >
        <CheckCircle size={14} weight="fill" />
        <span>
          Notification sent via{" "}
          <span className="font-medium">
            {result.via === "sw" ? "service worker" : "in-page"}
          </span>
          {result.swError && " (SW failed, page fallback used)"}
        </span>
      </div>
    );
  }
  const copy = REASON_COPY[result.reason] || REASON_COPY.unknown;
  const toneClass =
    copy.tone === "error"
      ? "bg-red-500/10 border-red-500/30 text-red-300"
      : "bg-amber-500/10 border-amber-500/30 text-amber-300";
  return (
    <div
      className={`rounded-xl border px-3 py-2 text-xs flex items-start gap-2 ${toneClass}`}
      data-testid="test-result-fail"
    >
      <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
      <div>
        <div className="font-medium">{copy.label}</div>
        <div className="opacity-80 mt-0.5">{copy.detail}</div>
        {result.detail && (
          <div className="mt-1 font-mono text-[10px] opacity-60 break-all">
            {result.detail}
          </div>
        )}
      </div>
    </div>
  );
}

function DiagList({ diag, permission, tz }) {
  const lp = diag.lastPoll || readLastPoll();
  const lastPollRel = lp.at ? relTime(lp.at) : "never";
  const items = [
    { k: "Permission", v: permission, color: dotForPermission(permission) },
    {
      k: "Service worker",
      v: diag.swRegistered ? "registered" : "not registered",
      color: diag.swRegistered ? "bg-emerald-400" : "bg-red-400",
    },
    {
      k: "SW controller",
      v: diag.swControlling ? "active" : "none",
      color: diag.swControlling ? "bg-emerald-400" : "bg-amber-400",
    },
    {
      k: "Last reminder poll",
      v: lp.at ? `${lastPollRel} · ${lp.result || "—"}` : "never",
      color: lp.at && Date.now() - new Date(lp.at).getTime() < 60000 ? "bg-emerald-400" : "bg-amber-400",
    },
    {
      k: "PWA mode",
      v: diag.standalone ? "standalone (installed)" : "browser tab",
      color: diag.standalone ? "bg-emerald-400" : "bg-white/30",
    },
    { k: "Timezone", v: tz || diag.timezone, color: "bg-white/30" },
    { k: "User agent", v: diag.uaShort, color: "bg-white/30", mono: true },
    { k: "Platform", v: diag.platform, color: "bg-white/30" },
  ];
  return (
    <ul className="space-y-1.5" data-testid="diag-list">
      {items.map(({ k, v, color, mono }) => (
        <li
          key={k}
          className="grid grid-cols-[120px_1fr_auto] gap-2 items-center text-xs"
          data-testid={`diag-row-${k.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <span className="text-white/50">{k}</span>
          <span className={`text-white/80 truncate ${mono ? "font-mono text-[11px]" : ""}`}>
            {v || "—"}
          </span>
          <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
        </li>
      ))}
    </ul>
  );
}

function dotForPermission(p) {
  if (p === "granted") return "bg-emerald-400";
  if (p === "denied") return "bg-red-400";
  if (p === "default") return "bg-amber-400";
  return "bg-white/30";
}

function readDiagSnapshot() {
  // Synchronous parts (used for initial render)
  return {
    swRegistered: false, // filled async
    swControlling:
      typeof navigator !== "undefined" &&
      "serviceWorker" in navigator &&
      !!navigator.serviceWorker.controller,
    standalone:
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true),
    timezone:
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : "",
    uaShort: shortUA(typeof navigator !== "undefined" ? navigator.userAgent : ""),
    platform:
      typeof navigator !== "undefined"
        ? navigator.userAgentData?.platform || navigator.platform || "—"
        : "—",
    lastPoll: readLastPoll(),
  };
}

async function readDiagSnapshotAsync() {
  const base = readDiagSnapshot();
  let swRegistered = false;
  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      swRegistered = regs.length > 0;
    } catch {}
  }
  return { ...base, swRegistered };
}

function shortUA(ua) {
  if (!ua) return "—";
  // Trim to a readable chunk: keep browser + OS hints
  const parts = [];
  const browserMatch =
    ua.match(/Chrome\/[\d.]+/) ||
    ua.match(/Firefox\/[\d.]+/) ||
    ua.match(/Safari\/[\d.]+/) ||
    ua.match(/Edg\/[\d.]+/);
  if (browserMatch) parts.push(browserMatch[0]);
  const osMatch =
    ua.match(/Android \d+(?:\.\d+)?/) ||
    ua.match(/iPhone OS [\d_]+/) ||
    ua.match(/Mac OS X [\d_]+/) ||
    ua.match(/Windows NT [\d.]+/);
  if (osMatch) parts.push(osMatch[0].replace(/_/g, "."));
  return parts.length > 0 ? parts.join(" · ") : ua.slice(0, 60);
}

function relTime(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    if (diff < 5000) return "just now";
    if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
